/**
 * D874: the pure view model behind the Admin dashboard's "Custos de plataforma" block (the Mapas and IA cards) and the "Gastos de mapas" screen.
 *
 * The servers do the billing arithmetic (`GET /v1/config/integrations/geo/spend`, `GET /v1/ai/spend`: exact integer micros of each vendor's own currency). This module only shapes it
 * for a person: the figures of one card are summed per currency, converted to the display currency with the stored rate when there is one (`approx`: every amount then reads "≈"),
 * the change against the same period of last month is a whole percentage, and the chart is the month's cumulative spend by day with a straight forecast line to the last day. With no stored rate
 * the card keeps the vendor currency and says so - a missing rate never turns into a made-up one.
 *
 * Neutral: no currency, vendor or country literal; the display currency is whatever the exchange-rate source resolved.
 */
import type { AiSpendReportView, GeoSpendView, GeoSpendVendorView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

/** What the signed-in operator may see of the platform costs: the maps spend reads `integration:view`, the AI spend `ai_spend:view`, both at the platform. */
export function platformCostsAuthority(grants: KnownGrants): { readonly mayViewMaps: boolean; readonly mayViewAi: boolean } {
  if (grants === undefined) return { mayViewMaps: false, mayViewAi: false };
  return {
    mayViewMaps: grantsAllow(grants, { permission: 'integration:view', target: 'PLATFORM' }),
    mayViewAi: grantsAllow(grants, { permission: 'ai_spend:view', target: 'PLATFORM' }),
  };
}

/** The `YYYY-MM` before `month`. */
export function previousMonthOf(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** An amount in micros (millionths of the major unit) of one currency. */
export interface CostAmount {
  readonly currency: string;
  readonly micros: bigint;
}

/** One currency's figures of a month: what a vendor (or the AI) spent, in that vendor's own currency. */
export interface CostStream {
  /** Where it comes from (a vendor id or `ai`), for the detail line. */
  readonly source: string;
  readonly currency: string;
  readonly spent: bigint;
  readonly samePeriod: bigint;
  /** The previous month in full; undefined when it could not be read. */
  readonly lastMonthTotal: bigint | undefined;
  readonly forecast: { readonly low: bigint; readonly mid: bigint; readonly high: bigint } | undefined;
  readonly shortHistory: boolean;
  readonly daysInMonth: number;
  /** The day of the month the figures are as of (1-based). */
  readonly today: number;
  /** Cumulative spend by day, day 1 first, `today` entries. */
  readonly thisMonth: readonly bigint[];
  /** The previous month's cumulative spend by day, every day of it. */
  readonly lastMonth: readonly bigint[];
}

const big = (text: string | undefined): bigint => (text !== undefined && /^-?\d+$/.test(text) ? BigInt(text) : 0n);
const bigOrUndefined = (text: string | undefined): bigint | undefined => (text !== undefined && /^-?\d+$/.test(text) ? BigInt(text) : undefined);
const clampDay = (day: number, daysInMonth: number): number => Math.max(1, Math.min(daysInMonth, Math.floor(day)));

/** The streams of the maps spend: one per vendor with a price book entry. */
export function mapsStreams(view: GeoSpendView | undefined): readonly CostStream[] {
  const out: CostStream[] = [];
  for (const v of view?.vendors ?? []) {
    const series = v.series;
    const daysInMonth = series?.daysInMonth ?? 0;
    out.push({
      source: v.vendorId,
      currency: v.currency,
      spent: big(v.monthToDate.costMicros),
      samePeriod: big(v.samePeriodLastMonth.costMicros),
      lastMonthTotal: series === undefined ? undefined : big(series.lastMonthTotalMicros),
      forecast: { low: big(v.projection.lowMicros), mid: big(v.projection.midMicros), high: big(v.projection.highMicros) },
      shortHistory: v.projection.notEnoughHistory,
      daysInMonth,
      today: series?.today ?? 0,
      thisMonth: (series?.thisMonthMicros ?? []).map(big),
      lastMonth: (series?.lastMonthMicros ?? []).map(big),
    });
  }
  return out;
}

/** The day of the month (1-based) of a `YYYY-MM-DD` date. */
const dayOf = (date: string): number => Number(date.slice(8, 10));

/** The cumulative cost by day of the AI's daily rows of one currency. */
function aiCumulative(report: AiSpendReportView, currency: string, days: number): bigint[] {
  const perDay = new Map<number, bigint>();
  for (const d of report.daily) if (d.currency === currency) perDay.set(dayOf(d.date), (perDay.get(dayOf(d.date)) ?? 0n) + big(d.costMicros));
  const out: bigint[] = [];
  let run = 0n;
  for (let day = 1; day <= days; day++) {
    run += perDay.get(day) ?? 0n;
    out.push(run);
  }
  return out;
}

/** The streams of the AI spend, one per currency of the month's totals. `previous` is the report of the month before (for the chart and the month total). */
export function aiStreams(report: AiSpendReportView | undefined, previous: AiSpendReportView | undefined): readonly CostStream[] {
  if (report === undefined) return [];
  const out: CostStream[] = [];
  const daysInMonth = report.daysInMonth;
  // `daysElapsed` is a fraction (14.5 at noon on the 15th): the day in progress is its floor + 1.
  const today = clampDay(Math.floor(report.daysElapsed) + 1, daysInMonth);
  for (const total of report.totals) {
    const currency = total.currency;
    const projection = report.projection?.find((p) => p.currency === currency);
    const forecastRow = report.forecast.find((f) => f.currency === currency);
    const mid = projection !== undefined ? big(projection.midMicros) : bigOrUndefined(forecastRow?.projectedMicros);
    const same = report.samePeriodLastMonth?.totals.find((t) => t.currency === currency);
    const lastTotal = previous?.totals.find((t) => t.currency === currency);
    out.push({
      source: 'ai',
      currency,
      spent: big(total.costMicros),
      samePeriod: same === undefined ? 0n : big(same.costMicros),
      lastMonthTotal: previous === undefined ? undefined : lastTotal === undefined ? 0n : big(lastTotal.costMicros),
      forecast: mid === undefined ? undefined : projection !== undefined ? { low: big(projection.lowMicros), mid, high: big(projection.highMicros) } : { low: mid, mid, high: mid },
      shortHistory: projection?.notEnoughHistory ?? false,
      daysInMonth,
      today,
      thisMonth: aiCumulative(report, currency, today),
      lastMonth: previous === undefined ? [] : aiCumulative(previous, currency, previous.daysInMonth),
    });
  }
  return out;
}

// ---- exchange ------------------------------------------------------------------------------------------------------------------------

/** What converts the currencies of a card: the display currency and the rate (micros of display per 1 of `from`) of each currency that has one. */
export interface CostFx {
  readonly display: string | undefined;
  readonly rates: ReadonlyMap<string, bigint>;
  /** The day the newest rate is for, and whether it is older than the source allows. */
  readonly rateDate: string | undefined;
  readonly stale: boolean;
}

/**
 * The exchange rates the dashboard can use: the stored ones the maps read carries (`approx`: from the vendor's currency to the display currency) first, then the AI spend's recorded ones
 * towards the same display currency (when the maps read has none, the display currency is the target of the first AI rate from a currency in `wanted`).
 */
export function costFx(geo: GeoSpendView | undefined, ai: AiSpendReportView | undefined, wanted: readonly string[]): CostFx {
  const rates = new Map<string, bigint>();
  let display: string | undefined;
  let rateDate: string | undefined;
  let stale = false;
  for (const v of geo?.vendors ?? []) {
    const a = v.approx;
    if (a === null) continue;
    if (display === undefined) display = a.currency;
    if (a.currency !== display) continue;
    if (!rates.has(v.currency)) rates.set(v.currency, big(a.rateMicros));
    if (rateDate === undefined || a.rateDate > rateDate) rateDate = a.rateDate;
    stale = stale || a.stale;
  }
  const aiRates = ai?.rates ?? [];
  if (display === undefined) display = aiRates.find((r) => wanted.includes(r.from))?.to;
  if (display !== undefined) {
    for (const from of wanted) {
      if (rates.has(from) || from === display) continue;
      const newest = aiRates.filter((r) => r.from === from && r.to === display).sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1))[0];
      if (newest !== undefined) {
        rates.set(from, big(newest.rateMicros));
        const date = newest.effectiveFrom.slice(0, 10);
        if (rateDate === undefined || date > rateDate) rateDate = date;
      }
    }
  }
  return { display, rates, rateDate, stale };
}

/** Convert micros at a rate, rounded half up (display only). */
export const convertMicros = (micros: bigint, rateMicros: bigint): bigint => (micros * rateMicros + 500_000n) / 1_000_000n;

// ---- the card ------------------------------------------------------------------------------------------------------------------------

export type CostChange =
  | { readonly kind: 'none' } // nothing to compare with (the same period of last month was zero)
  | { readonly kind: 'flat' }
  | { readonly kind: 'up' | 'down'; readonly percent: number };

/** The change of `now` against `before` as a whole percentage (rounded half away from zero); a change that rounds to 0 is `flat`; no base is `none`. */
export function costChange(now: bigint, before: bigint): CostChange {
  if (before <= 0n) return now > 0n ? { kind: 'none' } : { kind: 'flat' };
  const delta = (now - before) * 100n;
  const half = before / 2n;
  const percent = Number(delta >= 0n ? (delta + half) / before : -((-delta + half) / before));
  if (percent === 0) return { kind: 'flat' };
  return { kind: percent > 0 ? 'up' : 'down', percent: Math.abs(percent) };
}

export interface CostChart {
  readonly currency: string;
  /** Day numbers `1..n` as text (n = the longer of the two months). */
  readonly labels: readonly string[];
  /** Major units of the currency (a chart is read, not summed: floating point is fine here). */
  readonly thisMonth: readonly (number | null)[];
  readonly forecast: readonly (number | null)[];
  readonly lastMonth: readonly (number | null)[];
}

export interface CostCardModel {
  readonly id: 'maps' | 'ai';
  /** No spend and no history: the card says so instead of showing zeros. */
  readonly empty: boolean;
  /** True when the figures are converted at a stored rate (every amount reads "≈"). */
  readonly approx: boolean;
  /** Some currency has no stored rate yet: its amounts stay in the vendor's currency. */
  readonly missingRate: boolean;
  readonly spent: readonly CostAmount[];
  readonly samePeriod: readonly CostAmount[];
  readonly lastMonthTotal: readonly CostAmount[] | undefined;
  readonly forecast: { readonly low: readonly CostAmount[]; readonly mid: readonly CostAmount[]; readonly high: readonly CostAmount[] } | undefined;
  readonly shortHistory: boolean;
  /** Only when the figures are one currency. */
  readonly change: CostChange | undefined;
  readonly chart: CostChart | undefined;
  /** The figures as the vendors bill them (vendor currency), for the hover detail. */
  readonly original: readonly { readonly source: string; readonly currency: string; readonly spent: bigint }[];
  readonly rateDate: string | undefined;
  readonly rateStale: boolean;
  /** The day of the month the card is as of, and the days of the month. */
  readonly today: number;
  readonly daysInMonth: number;
}

const sumBy = (items: readonly CostAmount[]): CostAmount[] => {
  const by = new Map<string, bigint>();
  for (const i of items) by.set(i.currency, (by.get(i.currency) ?? 0n) + i.micros);
  return [...by].map(([currency, micros]) => ({ currency, micros }));
};

/** Element-wise sum of cumulative series of different lengths: a shorter one holds its last value (a month already over keeps its total). */
const sumSeries = (series: readonly (readonly bigint[])[], length: number): bigint[] => {
  const out: bigint[] = [];
  for (let i = 0; i < length; i++) out.push(series.reduce((a, s) => a + (s.length === 0 ? 0n : (s[Math.min(i, s.length - 1)] ?? 0n)), 0n));
  return out;
};

/** Build one card from its streams and the exchange rates. */
export function buildCostCard(id: 'maps' | 'ai', streams: readonly CostStream[], fx: CostFx): CostCardModel {
  const convertible = (currency: string): boolean => fx.display !== undefined && (currency === fx.display || fx.rates.has(currency));
  const convert = (micros: bigint, currency: string): CostAmount => {
    if (fx.display !== undefined && currency !== fx.display) {
      const rate = fx.rates.get(currency);
      if (rate !== undefined) return { currency: fx.display, micros: convertMicros(micros, rate) };
    }
    return { currency, micros };
  };
  const missingRate = streams.some((s) => !convertible(s.currency));
  // converted only when EVERY stream can be (a mix of converted and original amounts would be a sum of unlike things)
  const converted = !missingRate && streams.some((s) => s.currency !== fx.display);
  const at = (s: CostStream, micros: bigint): CostAmount => (missingRate ? { currency: s.currency, micros } : convert(micros, s.currency));
  const spent = sumBy(streams.map((s) => at(s, s.spent)));
  const samePeriod = sumBy(streams.map((s) => at(s, s.samePeriod)));
  const lastMonthTotal = streams.some((s) => s.lastMonthTotal === undefined) ? undefined : sumBy(streams.map((s) => at(s, s.lastMonthTotal ?? 0n)));
  const withForecast = streams.filter((s) => s.forecast !== undefined);
  const forecast =
    withForecast.length === 0 || withForecast.length < streams.length
      ? undefined
      : {
          low: sumBy(streams.map((s) => at(s, s.forecast?.low ?? 0n))),
          mid: sumBy(streams.map((s) => at(s, s.forecast?.mid ?? 0n))),
          high: sumBy(streams.map((s) => at(s, s.forecast?.high ?? 0n))),
        };
  const everything = streams.length === 0 || (streams.every((s) => s.spent === 0n && s.samePeriod === 0n) && streams.every((s) => (s.lastMonthTotal ?? 0n) === 0n));

  const single = spent.length === 1 ? spent[0] : undefined;
  const change = single === undefined || samePeriod.length !== 1 ? undefined : costChange(single.micros, samePeriod[0]?.micros ?? 0n);

  let chart: CostChart | undefined;
  if (single !== undefined && streams.length > 0 && !everything) {
    const daysInMonth = Math.max(...streams.map((s) => s.daysInMonth));
    const today = Math.max(1, Math.min(daysInMonth, Math.max(...streams.map((s) => s.today))));
    const lastLength = Math.max(0, ...streams.map((s) => s.lastMonth.length));
    const length = Math.max(daysInMonth, lastLength);
    const rateOf = (s: CostStream): bigint | undefined => (missingRate || s.currency === single.currency ? undefined : fx.rates.get(s.currency));
    const scaled = (s: CostStream, cum: readonly bigint[]): bigint[] => cum.map((m) => (rateOf(s) === undefined ? m : convertMicros(m, rateOf(s) as bigint)));
    const thisCum = sumSeries(streams.map((s) => scaled(s, s.thisMonth)), today);
    const lastCum = sumSeries(streams.map((s) => scaled(s, s.lastMonth)), lastLength);
    const toNumber = (m: bigint): number => Number(m) / 1_000_000;
    const thisValues: (number | null)[] = Array.from({ length }, (_, i) => (i < today ? toNumber(thisCum[i] ?? 0n) : null));
    const forecastValues: (number | null)[] = Array.from({ length }, () => null);
    const mid = forecast?.mid[0]?.micros;
    if (mid !== undefined && today < daysInMonth) {
      const from = thisCum[today - 1] ?? 0n;
      const to = mid > from ? mid : from;
      const span = daysInMonth - today;
      for (let day = today; day <= daysInMonth; day++) forecastValues[day - 1] = toNumber(from + ((to - from) * BigInt(day - today)) / BigInt(span));
    }
    const lastValues: (number | null)[] = Array.from({ length }, (_, i) => (i < lastLength ? toNumber(lastCum[i] ?? 0n) : null));
    chart = { currency: single.currency, labels: Array.from({ length }, (_, i) => String(i + 1)), thisMonth: thisValues, forecast: forecastValues, lastMonth: lastValues };
  }

  return {
    id,
    empty: everything,
    approx: converted,
    missingRate,
    spent,
    samePeriod,
    lastMonthTotal,
    forecast,
    shortHistory: streams.some((s) => s.shortHistory),
    change,
    chart,
    original: streams.map((s) => ({ source: s.source, currency: s.currency, spent: s.spent })),
    rateDate: converted ? fx.rateDate : undefined,
    rateStale: converted && fx.stale,
    today: streams.length === 0 ? 0 : Math.max(...streams.map((s) => s.today)),
    daysInMonth: streams.length === 0 ? 0 : Math.max(...streams.map((s) => s.daysInMonth)),
  };
}

// ---- the maps screen --------------------------------------------------------------------------------------------------------------------

/** The progress of a SKU's free allowance: units used, units free, and whether it has one. */
export interface FreeAllowance {
  readonly used: bigint;
  readonly free: bigint;
  readonly basisPoints: number;
}

export function freeAllowanceOf(line: GeoSpendVendorView['monthToDate']['lines'][number]): FreeAllowance | undefined {
  const free = big(line.freeUnits);
  if (free <= 0n) return undefined;
  return { used: big(line.units), free, basisPoints: line.freeConsumedBasisPoints };
}

/** The share of a vendor's attributed cost per key (market or purpose), biggest first, in the vendor's currency. */
export function attributionBy(vendor: GeoSpendVendorView, key: 'marketId' | 'purpose'): readonly { readonly key: string; readonly units: bigint; readonly costMicros: bigint }[] {
  const by = new Map<string, { units: bigint; costMicros: bigint }>();
  for (const a of vendor.attribution) {
    const row = by.get(a[key]) ?? { units: 0n, costMicros: 0n };
    by.set(a[key], { units: row.units + big(a.units), costMicros: row.costMicros + big(a.costMicros) });
  }
  return [...by].map(([k, v]) => ({ key: k, ...v })).sort((a, b) => (a.costMicros === b.costMicros ? (a.key < b.key ? -1 : 1) : a.costMicros < b.costMicros ? 1 : -1));
}
